import { useState, useEffect, useRef, useCallback, useMemo } from "react";
import axios from "axios";
import { LANGUAGES } from "@/components/chat/LanguageSelector";
import { chatNotificationSound } from "@/utils/chatNotificationSound";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WELCOME_MESSAGE = "Welcome to George Energy. I can help you understand our approach to renewable energy infrastructure development.";

// Create axios instance with optimized settings
const chatApi = axios.create({
  baseURL: API,
  timeout: 30000,
});

export const useChat = () => {
  const [messages, setMessages] = useState([{
    role: "assistant",
    content: WELCOME_MESSAGE,
    id: Date.now()
  }]);
  const [inputMessage, setInputMessage] = useState("");
  const [sessionId, setSessionId] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [isOpen, setIsOpen] = useState(false);
  const [isRecording, setIsRecording] = useState(false);
  const [audioBlob, setAudioBlob] = useState(null);
  const [selectedLanguage, setSelectedLanguage] = useState("en");
  const [respondInSameLanguage, setRespondInSameLanguage] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isLiveChatActive, setIsLiveChatActive] = useState(false); // Track live chat state

  const mediaRecorderRef = useRef(null);
  const audioChunksRef = useRef([]);
  const isOpenRef = useRef(isOpen);
  const sessionIdRef = useRef(null);
  const isCreatingSession = useRef(false);

  // Keep refs in sync
  useEffect(() => {
    isOpenRef.current = isOpen;
    if (isOpen) {
      setUnreadCount(0);
    }
  }, [isOpen]);

  useEffect(() => {
    sessionIdRef.current = sessionId;
  }, [sessionId]);

  // Create session immediately and in background
  const createSession = useCallback(async () => {
    if (isCreatingSession.current || sessionIdRef.current) return sessionIdRef.current;
    
    isCreatingSession.current = true;
    try {
      const response = await chatApi.post('/chat/session');
      const newSessionId = response.data.session_id;
      setSessionId(newSessionId);
      sessionIdRef.current = newSessionId;
      return newSessionId;
    } catch (error) {
      console.error("Error creating session:", error);
      return null;
    } finally {
      isCreatingSession.current = false;
    }
  }, []);

  // Create session on mount - non-blocking
  useEffect(() => {
    createSession();
  }, [createSession]);

  // Detect service from chat context - memoized
  const detectServiceFromChat = useCallback(() => {
    const recentMessages = messages.slice(-6).map(m => m.content.toLowerCase()).join(' ');
    
    if (recentMessages.includes('wind') || recentMessages.includes('turbine')) {
      return 'wind';
    } else if (recentMessages.includes('storage') || recentMessages.includes('battery') || recentMessages.includes('batteries')) {
      return 'storage';
    } else if (recentMessages.includes('efficiency') || recentMessages.includes('audit') || recentMessages.includes('consulting')) {
      return 'efficiency';
    } else if (recentMessages.includes('solar') || recentMessages.includes('panel')) {
      return 'solar';
    }
    return 'solar';
  }, [messages]);

  // Optimized send message with immediate UI update
  const sendMessage = useCallback(async (customMessage = null) => {
    const messageToSend = customMessage || inputMessage.trim();
    if (!messageToSend || isLoading) return;

    // Clear input immediately for responsiveness
    if (!customMessage) {
      setInputMessage("");
    }

    // Add user message immediately (optimistic update)
    const userMessageId = Date.now();
    setMessages(prev => [...prev, { 
      role: "user", 
      content: messageToSend,
      id: userMessageId
    }]);
    
    // Show loading state immediately
    setIsLoading(true);

    // Ensure we have a session
    let currentSessionId = sessionIdRef.current;
    if (!currentSessionId) {
      currentSessionId = await createSession();
      if (!currentSessionId) {
        setMessages(prev => [...prev, {
          role: "assistant",
          content: "I'm having trouble connecting. Please refresh the page and try again.",
          id: Date.now()
        }]);
        setIsLoading(false);
        return;
      }
    }

    try {
      const response = await chatApi.post('/chat', {
        message: messageToSend,
        session_id: currentSessionId,
        respond_in_language: respondInSameLanguage ? selectedLanguage : "en"
      });

      const responseText = response.data.response.toLowerCase();
      const userMessageLower = messageToSend.toLowerCase();
      
      // Only show enquiry link if:
      // 1. The response mentions contact/enquiry/form AND
      // 2. The user wasn't asking to learn/understand/educate
      const isEducationalQuery = userMessageLower.includes('learn') || 
                                 userMessageLower.includes('educate') || 
                                 userMessageLower.includes('explain') || 
                                 userMessageLower.includes('understand') ||
                                 userMessageLower.includes('tell me about') ||
                                 userMessageLower.includes('what is') ||
                                 userMessageLower.includes('how does');
      
      const mentionsContact = responseText.includes('enquiry') || 
                             responseText.includes('form') || 
                             responseText.includes('contact') ||
                             responseText.includes('get in touch') ||
                             responseText.includes('reach out');
      
      // Don't show enquiry form for educational queries
      const showEnquiryLink = mentionsContact && !isEducationalQuery;

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.response,
        showEnquiryLink,
        source: 'ai', // Mark as AI response
        id: Date.now()
      }]);
      
      if (!isOpenRef.current) {
        setUnreadCount(prev => prev + 1);
      }
      
      // Note: AI responses don't trigger notification sound
      // Only live expert messages would (when isLiveChatActive is true)
    } catch (error) {
      console.error("Error sending message:", error);
      
      // Provide helpful fallback response in user's selected language
      const fallbackResponses = {
        en: "I'm having a temporary connection issue, but I'm still here to help! Here's what you can do:\n\n• **Call us directly:** +91 96070 70525\n• **Email us:** contact@georgeenergy.co\n• **Try sending your message again** in a moment\n\nOur team is ready to assist you with all your solar and renewable energy questions!",
        hi: "मुझे एक अस्थायी कनेक्शन समस्या हो रही है, लेकिन मैं अभी भी आपकी मदद के लिए यहाँ हूँ! आप यह कर सकते हैं:\n\n• **हमें सीधे कॉल करें:** +91 96070 70525\n• **हमें ईमेल करें:** contact@georgeenergy.co\n• कुछ देर में **अपना संदेश फिर से भेजें**\n\nहमारी टीम आपके सोलर और अक्षय ऊर्जा संबंधी सभी सवालों में मदद करने के लिए तैयार है!",
        mr: "मला तात्पुरती कनेक्शन समस्या येत आहे, पण मी तुम्हाला मदत करण्यासाठी इथे आहे! तुम्ही हे करू शकता:\n\n• **आम्हाला थेट कॉल करा:** +91 96070 70525\n• **आम्हाला ईमेल करा:** contact@georgeenergy.co\n• थोड्या वेळाने **तुमचा संदेश पुन्हा पाठवा**\n\nआमची टीम तुमच्या सोलर आणि नवीकरणीय ऊर्जा संबंधित सर्व प्रश्नांमध्ये मदत करण्यास तयार आहे!",
        bn: "আমি একটি সাময়িক সংযোগ সমস্যার সম্মুখীন হচ্ছি, কিন্তু আমি এখনও আপনাকে সাহায্য করতে এখানে আছি! আপনি এটা করতে পারেন:\n\n• **সরাসরি কল করুন:** +91 96070 70525\n• **ইমেল করুন:** contact@georgeenergy.co\n• কিছুক্ষণ পরে **আবার মেসেজ পাঠান**\n\nআমাদের টিম আপনার সোলার এবং নবায়নযোগ্য শক্তি সংক্রান্ত সমস্ত প্রশ্নে সাহায্য করতে প্রস্তুত!",
        ta: "எனக்கு தற்காலிக இணைப்பு சிக்கல் உள்ளது, ஆனால் நான் இன்னும் உங்களுக்கு உதவ இங்கே இருக்கிறேன்! நீங்கள் செய்யலாம்:\n\n• **நேரடியாக அழைக்கவும்:** +91 96070 70525\n• **மின்னஞ்சல்:** contact@georgeenergy.co\n• சிறிது நேரத்தில் **மீண்டும் செய்தி அனுப்பவும்**\n\nஎங்கள் குழு உங்கள் சோலார் மற்றும் புதுப்பிக்கத்தக்க ஆற்றல் தொடர்பான அனைத்து கேள்விகளுக்கும் உதவ தயாராக உள்ளது!",
        te: "నాకు తాత్కాలిక కనెక్షన్ సమస్య వస్తోంది, కానీ నేను మీకు సహాయం చేయడానికి ఇక్కడే ఉన్నాను! మీరు చేయగలిగేది:\n\n• **నేరుగా కాల్ చేయండి:** +91 96070 70525\n• **ఇమెయిల్ చేయండి:** contact@georgeenergy.co\n• కొద్ది సేపట్లో **మీ మెసేజ్ మళ్ళీ పంపండి**\n\nమా బృందం మీ సోలార్ మరియు పునరుత్పాదక శక్తి సంబంధిత అన్ని ప్రశ్నలకు సహాయం చేయడానికి సిద్ధంగా ఉంది!",
        gu: "મને અસ્થાયી કનેક્શન સમસ્યા આવી રહી છે, પરંતુ હું હજુ પણ તમને મદદ કરવા માટે અહીં છું! તમે આ કરી શકો છો:\n\n• **સીધો કોલ કરો:** +91 96070 70525\n• **ઈમેઈલ કરો:** contact@georgeenergy.co\n• થોડી વારમાં **ફરીથી મેસેજ મોકલો**\n\nઅમારી ટીમ તમારા સોલર અને નવીનીકરણીય ઊર્જા સંબંધિત તમામ પ્રશ્નોમાં મદદ કરવા તૈયાર છે!",
        kn: "ನನಗೆ ತಾತ್ಕಾಲಿಕ ಸಂಪರ್ಕ ಸಮಸ್ಯೆ ಇದೆ, ಆದರೆ ನಾನು ಇನ್ನೂ ನಿಮಗೆ ಸಹಾಯ ಮಾಡಲು ಇಲ್ಲಿದ್ದೇನೆ! ನೀವು ಮಾಡಬಹುದು:\n\n• **ನೇರವಾಗಿ ಕರೆ ಮಾಡಿ:** +91 96070 70525\n• **ಇಮೇಲ್ ಮಾಡಿ:** contact@georgeenergy.co\n• ಸ್ವಲ್ಪ ಸಮಯದ ನಂತರ **ಮತ್ತೆ ಸಂದೇಶ ಕಳುಹಿಸಿ**\n\nನಮ್ಮ ತಂಡವು ನಿಮ್ಮ ಸೋಲಾರ್ ಮತ್ತು ನವೀಕರಿಸಬಹುದಾದ ಶಕ್ತಿ ಸಂಬಂಧಿತ ಎಲ್ಲಾ ಪ್ರಶ್ನೆಗಳಿಗೆ ಸಹಾಯ ಮಾಡಲು ಸಿದ್ಧವಾಗಿದೆ!",
        ml: "എനിക്ക് താൽക്കാലിക കണക്ഷൻ പ്രശ്നമുണ്ട്, പക്ഷേ ഞാൻ ഇപ്പോഴും നിങ്ങളെ സഹായിക്കാൻ ഇവിടെയുണ്ട്! നിങ്ങൾക്ക് ചെയ്യാം:\n\n• **നേരിട്ട് വിളിക്കുക:** +91 96070 70525\n• **ഇമെയിൽ ചെയ്യുക:** contact@georgeenergy.co\n• കുറച്ച് സമയത്തിന് ശേഷം **സന്ദേശം വീണ്ടും അയയ്ക്കുക**\n\nനിങ്ങളുടെ സോളാർ, പുനരുപയോഗ ഊർജ്ജ ചോദ്യങ്ങളിൽ സഹായിക്കാൻ ഞങ്ങളുടെ ടീം തയ്യാറാണ്!",
        pa: "ਮੈਨੂੰ ਅਸਥਾਈ ਕਨੈਕਸ਼ਨ ਸਮੱਸਿਆ ਆ ਰਹੀ ਹੈ, ਪਰ ਮੈਂ ਅਜੇ ਵੀ ਤੁਹਾਡੀ ਮਦਦ ਕਰਨ ਲਈ ਇੱਥੇ ਹਾਂ! ਤੁਸੀਂ ਇਹ ਕਰ ਸਕਦੇ ਹੋ:\n\n• **ਸਿੱਧਾ ਕਾਲ ਕਰੋ:** +91 96070 70525\n• **ਈਮੇਲ ਕਰੋ:** contact@georgeenergy.co\n• ਥੋੜੀ ਦੇਰ ਬਾਅਦ **ਦੁਬਾਰਾ ਸੁਨੇਹਾ ਭੇਜੋ**\n\nਸਾਡੀ ਟੀਮ ਤੁਹਾਡੇ ਸੋਲਰ ਅਤੇ ਨਵਿਆਉਣਯੋਗ ਊਰਜਾ ਸਬੰਧੀ ਸਾਰੇ ਸਵਾਲਾਂ ਵਿੱਚ ਮਦਦ ਕਰਨ ਲਈ ਤਿਆਰ ਹੈ!",
        or: "ମୋତେ ଅସ୍ଥାୟୀ ସଂଯୋଗ ସମସ୍ୟା ଆସୁଛି, କିନ୍ତୁ ମୁଁ ଏବେ ବି ଆପଣଙ୍କୁ ସାହାଯ୍ୟ କରିବାକୁ ଏଠାରେ ଅଛି! ଆପଣ ଏହା କରିପାରିବେ:\n\n• **ସିଧାସଳଖ କଲ୍ କରନ୍ତୁ:** +91 96070 70525\n• **ଇମେଲ୍ କରନ୍ତୁ:** contact@georgeenergy.co\n• କିଛି ସମୟ ପରେ **ପୁଣି ବାର୍ତ୍ତା ପଠାନ୍ତୁ**\n\nଆମ ଦଳ ଆପଣଙ୍କ ସୋଲାର ଏବଂ ନବୀକରଣଯୋଗ୍ୟ ଶକ୍ତି ସମ୍ବନ୍ଧୀୟ ସମସ୍ତ ପ୍ରଶ୍ନରେ ସାହାଯ୍ୟ କରିବାକୁ ପ୍ରସ୍ତୁତ!",
        ur: "مجھے عارضی کنکشن کا مسئلہ آ رہا ہے، لیکن میں ابھی بھی آپ کی مدد کے لیے یہاں ہوں! آپ یہ کر سکتے ہیں:\n\n• **براہ راست کال کریں:** +91 96070 70525\n• **ای میل کریں:** contact@georgeenergy.co\n• تھوڑی دیر بعد **دوبارہ پیغام بھیجیں**\n\nہماری ٹیم آپ کے سولر اور قابل تجدید توانائی سے متعلق تمام سوالات میں مدد کے لیے تیار ہے!"
      };
      
      const lang = respondInSameLanguage ? selectedLanguage : "en";
      const fallbackMessage = fallbackResponses[lang] || fallbackResponses.en;
      
      setMessages(prev => [...prev, {
        role: "assistant",
        content: fallbackMessage,
        showEnquiryLink: true, // Show enquiry link so user can still reach out
        source: 'ai',
        id: Date.now()
      }]);
      
      if (!isOpenRef.current) {
        setUnreadCount(prev => prev + 1);
      }
    } finally {
      setIsLoading(false);
    }
  }, [inputMessage, isLoading, respondInSameLanguage, selectedLanguage, createSession]);

  // Start recording - optimized
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      
      const mimeTypes = [
        'audio/webm;codecs=opus',
        'audio/webm',
        'audio/ogg;codecs=opus',
        'audio/ogg',
        'audio/mp4',
        'audio/mpeg'
      ];
      
      let selectedMimeType = '';
      for (const mimeType of mimeTypes) {
        if (MediaRecorder.isTypeSupported(mimeType)) {
          selectedMimeType = mimeType;
          break;
        }
      }
      
      const options = selectedMimeType ? { mimeType: selectedMimeType } : {};
      const mediaRecorder = new MediaRecorder(stream, options);
      mediaRecorderRef.current = mediaRecorder;
      audioChunksRef.current = [];

      mediaRecorder.ondataavailable = (event) => {
        if (event.data.size > 0) {
          audioChunksRef.current.push(event.data);
        }
      };

      mediaRecorder.onstop = async () => {
        const mimeType = mediaRecorder.mimeType || 'audio/webm';
        let extension = 'webm';
        if (mimeType.includes('ogg')) extension = 'ogg';
        else if (mimeType.includes('mp4')) extension = 'mp4';
        else if (mimeType.includes('mpeg')) extension = 'mp3';
        
        const blob = new Blob(audioChunksRef.current, { type: mimeType });
        setAudioBlob(blob);
        stream.getTracks().forEach(track => track.stop());
        
        // Transcribe audio
        if (!blob || !sessionIdRef.current) return;
        setIsLoading(true);
        
        try {
          const formData = new FormData();
          formData.append('file', blob, `voice-message.${extension}`);
          formData.append('session_id', sessionIdRef.current);
          formData.append('language', selectedLanguage);

          const transcribeResponse = await chatApi.post('/chat/transcribe', formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
          });

          const transcribedText = transcribeResponse.data.text;
          const detectedLang = transcribeResponse.data.detected_language;
          
          setMessages(prev => [...prev, { 
            role: "user", 
            content: transcribedText,
            isVoice: true,
            language: detectedLang,
            id: Date.now()
          }]);

          // Now send the transcribed text to chat
          try {
            const response = await chatApi.post('/chat', {
              message: transcribedText,
              session_id: sessionIdRef.current,
              respond_in_language: respondInSameLanguage ? selectedLanguage : "en"
            });

            setMessages(prev => [...prev, {
              role: "assistant",
              content: response.data.response,
              id: Date.now()
            }]);
          } catch (chatError) {
            console.error("Error getting chat response:", chatError);
            setMessages(prev => [...prev, {
              role: "assistant",
              content: "I'm sorry, I couldn't process your request right now. Please try again.",
              id: Date.now()
            }]);
          }
          
          if (!isOpenRef.current) {
            setUnreadCount(prev => prev + 1);
          }
        } catch (error) {
          console.error("Error transcribing audio:", error);
          const langName = LANGUAGES.find(l => l.code === selectedLanguage)?.name.split(' ')[0] || 'selected language';
          setMessages(prev => [...prev, {
            role: "assistant",
            content: `I had trouble understanding your ${langName} voice message. Please try speaking more clearly or typing your message instead.`,
            id: Date.now()
          }]);
          if (!isOpenRef.current) {
            setUnreadCount(prev => prev + 1);
          }
        } finally {
          setIsLoading(false);
          setAudioBlob(null);
        }
      };

      mediaRecorder.start();
      setIsRecording(true);
    } catch (error) {
      console.error("Error accessing microphone:", error);
      alert("Unable to access microphone. Please check permissions.");
    }
  }, [selectedLanguage, respondInSameLanguage]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
    }
  }, [isRecording]);

  // Handle file upload - optimized
  const handleFileUpload = useCallback(async () => {
    if (!selectedFile || isLoading) return;
    
    // Ensure session exists
    let currentSessionId = sessionIdRef.current;
    if (!currentSessionId) {
      currentSessionId = await createSession();
      if (!currentSessionId) return;
    }

    setIsLoading(true);
    const formData = new FormData();
    formData.append('file', selectedFile);
    formData.append('session_id', currentSessionId);
    formData.append('message', inputMessage.trim() || `I'm attaching a file: ${selectedFile.name}`);

    // Optimistic update
    setMessages(prev => [...prev, {
      role: "user",
      content: inputMessage.trim() || `Attached file: ${selectedFile.name}`,
      hasFile: true,
      fileName: selectedFile.name,
      id: Date.now()
    }]);
    setInputMessage("");

    try {
      const response = await chatApi.post('/chat/upload', formData, {
        headers: { 'Content-Type': 'multipart/form-data' },
      });

      setMessages(prev => [...prev, {
        role: "assistant",
        content: response.data.response,
        id: Date.now()
      }]);
      
      if (!isOpenRef.current) {
        setUnreadCount(prev => prev + 1);
      }

      setSelectedFile(null);
    } catch (error) {
      console.error("Error uploading file:", error);
      setMessages(prev => [...prev, {
        role: "assistant",
        content: "I apologize, but I had trouble processing your file. Please try again.",
        id: Date.now()
      }]);
      if (!isOpenRef.current) {
        setUnreadCount(prev => prev + 1);
      }
    } finally {
      setIsLoading(false);
    }
  }, [selectedFile, inputMessage, isLoading, createSession]);

  // Handle topic click - faster response
  const handleTopicClick = useCallback((topic) => {
    setIsOpen(true);
    
    // Send message immediately without delay
    requestAnimationFrame(() => {
      let topicMessage = "";
      switch(topic) {
        case "solar":
          topicMessage = "I'd like to understand George Energy's approach to solar energy development.";
          break;
        case "wind":
          topicMessage = "Can you tell me about George Energy's wind energy focus and approach?";
          break;
        case "marine":
          topicMessage = "What is George Energy's involvement in marine energy?";
          break;
        case "approach":
          topicMessage = "What is George Energy's development approach and philosophy?";
          break;
        case "sectors":
          topicMessage = "Which sectors does George Energy work in?";
          break;
        case "distributed":
          topicMessage = "Can you explain distributed public infrastructure and how George Energy approaches it?";
          break;
        default:
          topicMessage = "I'd like to learn more about George Energy.";
      }
      sendMessage(topicMessage);
    });
  }, [sendMessage]);

  // Live chat controls for future expert integration
  const enableLiveChat = useCallback(() => {
    setIsLiveChatActive(true);
    chatNotificationSound.enableLiveChat();
  }, []);

  const disableLiveChat = useCallback(() => {
    setIsLiveChatActive(false);
    chatNotificationSound.disableLiveChat();
  }, []);

  // Function to add a live expert message (for future integration)
  const addLiveExpertMessage = useCallback((content) => {
    const newMessage = {
      role: "assistant",
      content,
      source: 'agent', // Live expert message
      id: Date.now()
    };
    
    setMessages(prev => [...prev, newMessage]);
    
    // Play notification sound if tab is inactive and live chat is active
    if (isLiveChatActive) {
      chatNotificationSound.play();
    }
    
    if (!isOpenRef.current) {
      setUnreadCount(prev => prev + 1);
    }
  }, [isLiveChatActive]);

  return {
    // State
    messages,
    setMessages, // Expose setMessages for restore/clear functionality
    inputMessage,
    setInputMessage,
    sessionId,
    isLoading,
    isOpen,
    setIsOpen,
    isRecording,
    selectedLanguage,
    setSelectedLanguage,
    respondInSameLanguage,
    setRespondInSameLanguage,
    selectedFile,
    setSelectedFile,
    unreadCount,
    isLiveChatActive,
    
    // Actions
    sendMessage,
    startRecording,
    stopRecording,
    handleFileUpload,
    handleTopicClick,
    detectServiceFromChat,
    enableLiveChat,
    disableLiveChat,
    addLiveExpertMessage
  };
};
